/**
 * A machine's surroundings as a little map: the tiles within its range, laid flat around its own
 * tile as they lie on the planet (their real outlines, pentagons too), each with what it is to the
 * machine: for an extractor its own tile, ground it draws from (alone or shared with another), or
 * something else; for a charge the rock it breaks, or not; for an infuser the tiles that take a
 * block, and the one its next block goes to.
 */
import {
  breaks,
  drawCounts,
  MACHINES,
  extractorAt,
  gridOf,
  infuserRing,
  isCharge,
  isInfuser,
  machineAt,
  takesBlock,
  type CrewState,
  type Grid,
  type Kind,
} from '@bramvasterd/terrella-rules';

export type ReachRole = 'self' | 'ground' | 'shared' | 'other' | 'breaks' | 'next';

export interface ReachTile {
  tile: number;
  kind: Kind;
  role: ReachRole;
  /** Its outline, flat: x to the right, y up, in planet units around the extractor's tile. */
  points: readonly (readonly [number, number])[];
}

/** The tiles within `range` steps of `tile`, ring by ring, the tile first. */
function within(grid: Grid, tile: number, range: number): number[] {
  const out = [tile];
  const seen = new Set(out);
  let ring = out;
  for (let step = 0; step < range; step++) {
    const next: number[] = [];
    for (const t of ring)
      for (const j of grid.neighbors[t] ?? [])
        if (!seen.has(j)) {
          seen.add(j);
          next.push(j);
        }
    out.push(...next);
    ring = next;
  }
  return out;
}

const dot = (a: readonly number[], b: readonly number[]): number =>
  (a[0] ?? 0) * (b[0] ?? 0) + (a[1] ?? 0) * (b[1] ?? 0) + (a[2] ?? 0) * (b[2] ?? 0);

/** The reach of the extractor on `tile`, or nothing when none stands there. */
/** Lays tiles flat around `tile`: each one's outline in planet units, x to the right, y up. */
function layFlat(
  grid: Grid,
  tile: number,
  tiles: readonly number[],
): Map<number, readonly (readonly [number, number])[]> {
  // a flat frame on the tile: its outward direction, and two axes across it
  const c = [...grid.centers.subarray(tile * 3, tile * 3 + 3)];
  const help = Math.abs(c[2] ?? 0) < 0.9 ? [0, 0, 1] : [1, 0, 0];
  const along = dot(help, c);
  const ex = help.map((v, k) => v - along * (c[k] ?? 0));
  const n = Math.hypot(...ex);
  const e1 = ex.map((v) => v / n);
  const e2 = [
    (c[1] ?? 0) * (e1[2] ?? 0) - (c[2] ?? 0) * (e1[1] ?? 0),
    (c[2] ?? 0) * (e1[0] ?? 0) - (c[0] ?? 0) * (e1[2] ?? 0),
    (c[0] ?? 0) * (e1[1] ?? 0) - (c[1] ?? 0) * (e1[0] ?? 0),
  ];
  const out = new Map<number, [number, number][]>();
  for (const t of tiles) {
    const corners = grid.corners[t] ?? new Float64Array();
    const points: [number, number][] = [];
    for (let k = 0; k < corners.length; k += 3) {
      const p = [corners[k] ?? 0, corners[k + 1] ?? 0, corners[k + 2] ?? 0];
      points.push([dot(p, e1), dot(p, e2)]);
    }
    out.set(t, points);
  }
  return out;
}

/** The reach of the extractor on `tile`, or nothing when none stands there. */
export function reachMap(state: CrewState, tile: number): ReachTile[] {
  const extractor = extractorAt(state, tile);
  if (!extractor) return [];
  const spec = MACHINES[extractor.type];
  const range = spec.levels[extractor.level - 1]?.range ?? 0;
  const grid = gridOf(state);
  const counts = drawCounts(state);
  const tiles = within(grid, tile, range);
  const flat = layFlat(grid, tile, tiles);
  return tiles.map((t) => {
    const kind = state.kind[t] as Kind;
    const drawn = counts.get(t) ?? 0;
    const role: ReachRole =
      t === tile ? 'self' : kind !== spec.ground ? 'other' : drawn > 1 ? 'shared' : 'ground';
    return { tile: t, kind, role, points: flat.get(t) ?? [] };
  });
}

/**
 * The tiles the charge on `tile` breaks as the planet is now, ascending: within its range, intact
 * rock it is strong enough for, with no other machine on it (the rules' blast). None without one.
 */
export function blastTiles(state: CrewState, tile: number): number[] {
  const charge = machineAt(state, tile);
  if (!charge || !isCharge(charge)) return [];
  const { power, range } = MACHINES[charge.type];
  return within(gridOf(state), tile, range)
    .filter((t) => (t === tile || !state.machines.has(t)) && breaks(state, t, power))
    .sort((a, b) => a - b);
}

/** The area of the charge on `tile` as a map round it: the rock it breaks, and the rest. */
export function blastMap(state: CrewState, tile: number): ReachTile[] {
  const charge = machineAt(state, tile);
  if (!charge || !isCharge(charge)) return [];
  const grid = gridOf(state);
  const tiles = within(grid, tile, MACHINES[charge.type].range);
  const flat = layFlat(grid, tile, tiles);
  const breaking = new Set(blastTiles(state, tile));
  return tiles.map((t) => ({
    tile: t,
    kind: state.kind[t] as Kind,
    role: t === tile ? 'self' : breaking.has(t) ? 'breaks' : 'other',
    points: flat.get(t) ?? [],
  }));
}

/** The ring of the infuser on `tile` as a map round it: where its next block goes, what takes one, the rest. */
export function ringMap(state: CrewState, tile: number, next: number | null): ReachTile[] {
  const infuser = machineAt(state, tile);
  if (!infuser || !isInfuser(infuser)) return [];
  const tiles = [tile, ...infuserRing(state, tile)];
  const flat = layFlat(gridOf(state), tile, tiles);
  return tiles.map((t) => ({
    tile: t,
    kind: state.kind[t] as Kind,
    role: t === tile ? 'self' : t === next ? 'next' : takesBlock(state, t) ? 'ground' : 'other',
    points: flat.get(t) ?? [],
  }));
}
